import { useState, useEffect } from 'react';
import { useAuth } from './use-auth';
import { supabaseHelpers } from '@/lib/supabase';

/**
 * Pronouns are optional. Empty string ("") is the user's explicit "prefer
 * not to say" — distinct from `null` which means "we never asked" but
 * serialized/stored identically in Supabase (`null`).
 */
export function usePronouns() {
  const { user } = useAuth();
  const [pronouns, setPronouns] = useState<string>(() => localStorage.getItem('vyzle-chat-pronouns') || '');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const saved = localStorage.getItem('vyzle-chat-pronouns');
        if (saved !== null) setPronouns(saved);

        if (user?.uid) {
          try {
            const row = await supabaseHelpers.getUser(user.uid);
            if (row?.pronouns !== undefined && row?.pronouns !== null) {
              setPronouns(row.pronouns);
              localStorage.setItem('vyzle-chat-pronouns', row.pronouns);
            }
          } catch (error) {
            console.warn('Failed to load pronouns from Supabase:', error);
          }
        }
      } catch (error) {
        console.error('Error loading pronouns:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  const updatePronouns = async (value: string) => {
    try {
      setPronouns(value);
      // Treat empty string as the explicit "prefer not to say" state —
      // store as null in Supabase, and remove the localStorage key so a
      // fresh read doesn't resurrect the old value.
      if (value) {
        localStorage.setItem('vyzle-chat-pronouns', value);
      } else {
        localStorage.removeItem('vyzle-chat-pronouns');
      }
      if (user?.uid) {
        await supabaseHelpers.updateUser(user.uid, { pronouns: value || null });
      }
    } catch (error) {
      console.error('Failed to update pronouns:', error);
      throw error;
    }
  };

  return { pronouns, updatePronouns, loading };
}
